import Image from 'next/image';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { VENUE } from './kit';

const STEPS = [
  {
    title: 'Tell us about your team',
    text: 'Share your goals and team size through the form. We plan the session around the work your team actually does.',
  },
  {
    title: 'Bring a laptop',
    text: 'That’s all you need. We provide the internet connection and set everyone up on the Unlimited plan.',
  },
  {
    title: 'Three hours, hands-on',
    text: 'We start with how generative AI works, then you practise on your own business cases with us in the room.',
  },
];

const TAKEAWAYS = [
  'The complete workshop slides and guide',
  'Frameworks and templates you can reuse',
  'A place in our community of business owners',
  'Support after the session',
];

const SESSION_PHOTO =
  'https://media.easy-peasy.ai/landing/about/workshop-june-2025.webp';

export default function WorkshopsSession() {
  const { ref, state } = useReveal<HTMLOListElement>(0.2);

  return (
    <section id='how-it-works' className='scroll-mt-8 pb-24 sm:pb-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='How a workshop runs.'
          tail='Three hours, all hands-on.'
        />

        <ol
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'mt-14 grid gap-4 md:grid-cols-3')}
        >
          {STEPS.map(({ title, text }, index) => (
            <li
              key={title}
              className={clsxm(
                styles.rv,
                'rounded-[22px] p-7',
                tone.surface,
                tone.line,
              )}
              style={delay(index * 90)}
            >
              <span className='flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold tabular-nums text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'>
                {index + 1}
              </span>
              <h3
                className={clsxm(
                  'mt-5 text-xl font-semibold tracking-[-0.015em]',
                  tone.ink,
                )}
              >
                {title}
              </h3>
              <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>
                {text}
              </p>
            </li>
          ))}
        </ol>

        <div className='mt-4 grid gap-4 lg:grid-cols-12'>
          <figure className='relative min-h-[18rem] overflow-hidden rounded-[22px] bg-[#e8e8f0] lg:col-span-5 dark:bg-white/5'>
            <Image
              src={SESSION_PHOTO}
              alt='Participants at an Easy-Peasy.AI workshop, June 2025'
              fill
              unoptimized
              className='object-cover object-[50%_60%]'
            />
          </figure>

          <div
            className={clsxm(
              'rounded-[22px] p-7 lg:col-span-3',
              tone.surface,
              tone.line,
            )}
          >
            <h3
              className={clsxm(
                'text-lg font-semibold tracking-[-0.01em]',
                tone.ink,
              )}
            >
              What you take home
            </h3>
            <ul className='mt-5 space-y-3.5'>
              {TAKEAWAYS.map((item) => (
                <li
                  key={item}
                  className={clsxm(
                    'flex gap-2.5 text-[15px] leading-6',
                    tone.body,
                  )}
                >
                  <CheckGlyph className='mt-0.5 h-[18px] w-[18px] flex-none' />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div
            className={clsxm(
              'flex flex-col overflow-hidden rounded-[22px] lg:col-span-4',
              tone.surface,
              tone.line,
            )}
          >
            <div className='p-7 pb-5'>
              <h3
                className={clsxm(
                  'text-lg font-semibold tracking-[-0.01em]',
                  tone.ink,
                )}
              >
                Where we meet
              </h3>
              <p className={clsxm('mt-3 text-[15px] leading-6', tone.body)}>
                {VENUE.name}
                <br />
                {VENUE.address}
              </p>
              <p className={clsxm('mt-2 text-sm leading-6', tone.quiet)}>
                In the CBD, a short walk from the MRT. Doors open 15 minutes
                before the start.
              </p>
              <a
                href={VENUE.maps}
                target='_blank'
                rel='noopener noreferrer'
                className='mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 underline-offset-4 hover:underline dark:text-primary-400'
              >
                Open in Google Maps
                <ArrowGlyph className='h-3.5 w-3.5 -rotate-45' />
              </a>
            </div>
            <iframe
              src={VENUE.embed}
              title='Map of Easy-Peasy.AI at JustCo, Cross Street Exchange, Singapore'
              loading='lazy'
              referrerPolicy='no-referrer-when-downgrade'
              className='h-48 w-full flex-1 border-0 grayscale-[0.25] lg:min-h-[12rem]'
            />
          </div>
        </div>
      </div>
    </section>
  );
}
